import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Icon, StyledRadios, StackedIcons } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { useSelector } from 'react-redux';
import { getAllPolicyHolders } from '../../helpers/policyHelpers';
import { DigitalPreference } from './digitalPreference/digitalPreference';
import { AddressDetails } from '../accountDetails/policyHolderDetails/addressDetails/addressDetails';
import { SLICES } from '../../constants/slices';
import './notifications.scss';
import { useGetContactDetailsQuery, useGetPetInfoQuery, useUpdatePreferredContactMutation } from '../../data/policy';
import Loading from '../shared/Loading/Loading';
import { SUCCESS_MESSAGES } from '../../constants/constants';
import { singlePetSuccessMessage } from '../../utils/successMessages';
import { legacyPolicyNumberHandler } from '../../helpers/paymentHelper';

export const Notifications = () => {
  const fieldFormContext = useFormContext();
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const { data: petDetails, isLoading: petDetailsLoading } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken
  });
  const { data: contactDetails, isLoading: contactDetailsLoading } = useGetContactDetailsQuery(
    {
      policyNumber: legacyPolicyNumberHandler(petDetails?.[0]?.PolicyNumber),
      auth0Token: auth0AccessToken
    },
    { skip: petDetailsLoading }
  );
  const [updatePreferredContact, { isSuccess }] = useUpdatePreferredContactMutation();
  const [notificationPreference, setNotificationPreference] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [allPolicyHolders, setAllPolicyHolders] = useState();

  useEffect(() => {
    if (petDetails) {
      const data = getAllPolicyHolders({ petDetails });
      setAllPolicyHolders(data);
    }
  }, [petDetails]);

  useEffect(() => {
    if (contactDetails) {
      setNotificationPreference(contactDetails.prefferedContact);
    }
  }, [contactDetails]);

  useEffect(() => {
    if (isSuccess) {
      setShowSuccessMessage(isSuccess);
    }
  }, [isSuccess]);

  const handlePreferenceChange = (value) => {
    setNotificationPreference(value);
  };

  const handleUpdatePreferredContact = () => {
    const preferredContact = {
      email: contactDetails.email,
      preferredContactMethod: notificationPreference
    };

    updatePreferredContact(preferredContact);
  };

  return showSuccessMessage ? (
    <>
      <p>
        <p>
          {singlePetSuccessMessage('notification preferences')}
          {petDetails.length > 1 && SUCCESS_MESSAGES.MULTI_PET}
        </p>
      </p>
      <button
        type="button"
        className="button primary"
        onClick={() => setShowSuccessMessage(false)}
      >
        <span className="labelText">Go back</span>
      </button>
    </>
  ) : (
    <div className="notificationPreferences">
      <h3>Select your preferred communication method</h3>
      <p>
        Email and SMS communication gets to you faster than the post. Ensure your notification preference is switched to
        &lsquo;Digital&rsquo; for fast and easy notifications.
      </p>

      {contactDetailsLoading || !contactDetails ? (
        <Loading />
      ) : (
        <StyledRadios
          {...{
            name: 'notificationPreferences',
            onChange: handlePreferenceChange,
            groupLabel: 'Select your preferred communication method',
            hideGroupLabel: true,
            groupName: 'notificationPreferences',
            radioInputs: [
              {
                id: 'digital',
                label: 'Digital (faster)',
                value: 0,
                labelCssClass: {
                  checked: 'button primary',
                  unchecked: 'button primary outline'
                },
                icon: {
                  checked: (
                    <StackedIcons
                      customCssClass="offsetTick"
                      icons={[
                        <Icon
                          key="icon1"
                          icon="radio-unchecked"
                        />,
                        <Icon
                          key="icon2"
                          icon="tick"
                          customCssClass="reveal animate-slow animate-once"
                        />
                      ]}
                    />
                  ),
                  unchecked: <Icon icon="radio-unchecked" />
                }
              },
              {
                id: 'post',
                label: 'Post (letter)',
                value: 1,
                labelCssClass: {
                  checked: 'button primary',
                  unchecked: 'button primary outline'
                },
                icon: {
                  checked: (
                    <StackedIcons
                      customCssClass="offsetTick"
                      icons={[
                        <Icon
                          key="icon1"
                          icon="radio-unchecked"
                        />,
                        <Icon
                          key="icon2"
                          icon="tick"
                          customCssClass="reveal animate-slow animate-once"
                        />
                      ]}
                    />
                  ),
                  unchecked: <Icon icon="radio-unchecked" />
                }
              }
            ],
            value: notificationPreference,
            fieldFormContext
          }}
        />
      )}

      <div className="currentPreferenceDetails">
        {petDetailsLoading || contactDetailsLoading || !allPolicyHolders ? (
          <Loading />
        ) : (
          <>
            {notificationPreference === 0 && (
              <DigitalPreference
                policyHolder={allPolicyHolders[0]}
                fieldFormContext={fieldFormContext}
                contactDetails={contactDetails}
              />
            )}

            {notificationPreference === 1 && (
              <AddressDetails
                policyHolder={allPolicyHolders[0]}
                numPolicyHolders={allPolicyHolders.length}
              />
            )}
          </>
        )}
      </div>

      <p>Any changes to your notification preferences will apply to all pets you have insured with us.</p>

      <p>
        Please note that some communications may still be sent by post, but most communications are accessible through
        the Pet Portal.
      </p>
      <div className="buttonContainer">
        <button
          type="button"
          className="button primary outline"
          onClick={() => setNotificationPreference(contactDetails?.prefferedContact)}
          disabled={notificationPreference === contactDetails?.prefferedContact}
        >
          <span className="labelText">Cancel</span>
        </button>
        <button
          type="button"
          className="button primary"
          onClick={() => handleUpdatePreferredContact()}
          disabled={notificationPreference === contactDetails?.prefferedContact}
        >
          <span className="labelText">Save</span>
        </button>
      </div>
    </div>
  );
};
